<template>
  <div
    :style="style"
    :class="[
      {
        ['ebar' + entryId]: true,
        [classNameActive]: enabled,
        [classNameDragging]: dragging,
        [classNameResizing]: resizing,
        [classNameDraggable]: draggable,
        [classNameResizable]: resizable,
      },
      className,
    ]"
    @mousedown.stop.prevent="elementMouseDown"
    @touchstart.stop.prevent="elementTouchDown"
  >
    <div
      v-for="handle in actualHandles"
      :key="handle"
      :class="[classNameHandle, classNameHandle + '-' + handle]"
      :style="{ display: enabled ? 'block' : 'none' }"
      @mousedown.stop.prevent="handleDown(handle, $event)"
      @touchstart.stop.prevent="handleTouchDown(handle, $event)"
    >
      <slot :name="handle"/>
    </div>
    <slot/>
  </div>
</template>
<script>
import VueDraggableResizable from 'vue-draggable-resizable'
import { checkIfTargetFromClass, clearTextSelection } from '@/components/utils'

const events = {
  mouse: {
    start: 'mousedown',
    move: 'mousemove',
    stop: 'mouseup',
  },
  touch: {
    start: 'touchstart',
    move: 'touchmove',
    stop: 'touchend',
  },
}

let eventsFor = events.mouse

export default {
  mixins: [VueDraggableResizable],

  props: {
    entryId: {
      type: String,
      default: 'lulu',
    },
  },

  data: function() {
    return {
      left: this.x,
      top: this.y,
      right: null,
      bottom: null,
      width: null,
      height: this.h,
      widthTouched: false,
      heightTouched: false,
      aspectFactor: null,
      parentWidth: null,
      parentHeight: null,
      minW: this.minWidth,
      minH: this.minHeight,
      maxW: this.maxWidth,
      maxH: this.maxHeight,
      handle: null,
      enabled: this.active,
      resizing: false,
      dragging: false,
      dragEnable: false,
      resizeEnable: false,
      zIndex: this.z,
    }
  },

  mounted: function() {
    this.addEvent(document.documentElement, 'touchstart', this.deselect)
  },

  beforeDestroy(){
    this.removeEvent(document.documentElement, 'touchstart', this.deselect)
    this.removeEvent(document.documentElement, eventsFor.move, this.handleResize)
  },

  methods: {
    removeEvent(el, event, handler) {
      if (!el) {
        return
      }
      if (el.detachEvent) {
        el.detachEvent('on' + event, handler)
      } else if (el.removeEventListener) {
        el.removeEventListener(event, handler, true)
      } else {
        el['on' + event] = null
      }
    },
    elementTouchDown(e) {
      eventsFor = events.touch

      this.elementDown(e)
    },
    elementMouseDown(e) {
      eventsFor = events.mouse

      this.elementDown(e)
    },
    deselect(e) {
      // console.log('deselect, is prevent', this.preventDeactivation)
      const target = e.target || e.srcElement

      if (checkIfTargetFromClass(target, 'entryPopup')) {
        // console.log('deselect from entryPOPUP!')
        return
      }

      const regex = new RegExp(this.className + '-([trmbl]{2})', '')

      if (!this.$el.contains(target) && !regex.test(target.className)) {
        if (!checkIfTargetFromClass(target, 'ebar' + this.entryId)) {
          if (this.enabled && !this.preventDeactivation) {
            this.enabled = false
            this.$emit('deactivated')
            this.$emit('update:active', false)
          }

          this.removeEvent(document.documentElement, eventsFor.move, this.handleResize)
        }
      }
      
      this.resetBoundsAndMouseState()
    },
    handleDown(handle, e) {
      if (e instanceof MouseEvent && e.which !== 1) {
        return
      }

      if (this.onResizeStart(handle, e) === false) {
        return
      }

      if (e.stopPropagation) e.stopPropagation()

      // Here we avoid a dangerous recursion by faking
      // corner handles as middle handles
      if (this.lockAspectRatio && !handle.includes('m')) {
        this.handle = 'm' + handle.substring(1)
      } else {
        this.handle = handle
      }

      this.resizeEnable = true

      this.mouseClickPosition.mouseX = e.touches ? e.touches[0].pageX : e.pageX
      this.mouseClickPosition.mouseY = e.touches ? e.touches[0].pageY : e.pageY
      this.mouseClickPosition.left = this.left
      this.mouseClickPosition.right = this.right
      this.mouseClickPosition.top = this.top
      this.mouseClickPosition.bottom = this.bottom

      this.bounds = this.calcResizeLimits()

      this.addEvent(document.documentElement, eventsFor.move, this.handleResize)
      this.addEvent(document.documentElement, eventsFor.stop, this.handleUp)
    },
    handleUp(e) {
      this.handle = null

      this.resetBoundsAndMouseState()

      this.dragEnable = false
      this.resizeEnable = false

      if (this.resizing) {
        this.resizing = false
        this.$emit('resizestop', this.left, this.top, this.width, this.height)
      }

      if (this.dragging) {
        this.dragging = false
        this.$emit('dragstop', this.left, this.top)
      }

      this.removeEvent(document.documentElement, eventsFor.move, this.handleResize)
    },
    elementDown(e) {
      // console.log('+++++ element down')
      clearTextSelection()

      if (e instanceof MouseEvent && e.which !== 1) {
        return
      }
      const target = e.target || e.srcElement

      if (this.$el.contains(target)) {
        if (this.onDragStart(e) === false) {
          return
        }

        if (
          (this.dragHandle && !matchesSelectorToParentElements(target, this.dragHandle, this.$el)) ||
          (this.dragCancel && matchesSelectorToParentElements(target, this.dragCancel, this.$el))
        ) {
          this.dragging = false

          return
        }

        if (!this.enabled) {
          this.enabled = true
          this.$emit('update:active', true)
        }

        this.$emit('activated')

        if (this.draggable) {
          this.dragEnable = true
        }

        this.mouseClickPosition.mouseX = e.touches ? e.touches[0].pageX : e.pageX
        this.mouseClickPosition.mouseY = e.touches ? e.touches[0].pageY : e.pageY

        this.mouseClickPosition.left = this.left
        this.mouseClickPosition.right = this.right
        this.mouseClickPosition.top = this.top
        this.mouseClickPosition.bottom = this.bottom

        if (this.parent) {
          this.bounds = this.calcDragLimits()
        }

        this.addEvent(document.documentElement, eventsFor.move, this.move)
        this.addEvent(document.documentElement, eventsFor.stop, this.handleUp)
      }
    },
    snapToGrid(grid, pendingX, pendingY, scale = 1) {
      const x = Math.round(pendingX / scale / grid[0]) * grid[0]
      const y = Math.round(pendingY / scale / grid[1]) * grid[1]

      return [x, y]
    },
    restrictToBounds(value, min, max) {
      if (min !== null && value < min) {
        return min
      }

      if (max !== null && max < value) {
        return max
      }

      return value
    },
    computeWidth(parentWidth, left, right) {
      return parentWidth - left - right
    },
    computeHeight(parentHeight, top, bottom) {
      return parentHeight - top - bottom
    },
    addEvent(el, event, handler) {
      if (!el) {
        return
      }
      if (el.attachEvent) {
        el.attachEvent('on' + event, handler)
      } else if (el.addEventListener) {
        el.addEventListener(event, handler, true)
      } else {
        el['on' + event] = handler
      }
    },
    handleResize(e) {
      let left = this.left
      let top = this.top
      let right = this.right
      let bottom = this.bottom

      const mouseClickPosition = this.mouseClickPosition
      const lockAspectRatio = this.lockAspectRatio
      const aspectFactor = this.aspectFactor

      const tmpDeltaX = mouseClickPosition.mouseX - (e.touches ? e.touches[0].pageX : e.pageX)
      const tmpDeltaY = mouseClickPosition.mouseY - (e.touches ? e.touches[0].pageY : e.pageY)

      if (!this.widthTouched && tmpDeltaX) {
        this.widthTouched = true
      }

      if (!this.heightTouched && tmpDeltaY) {
        this.heightTouched = true
      }

      const [deltaX, deltaY] = this.snapToGrid(this.grid, tmpDeltaX, tmpDeltaY, this.scale)

      if (this.handle.includes('b')) {
        bottom = this.restrictToBounds(mouseClickPosition.bottom + deltaY, this.bounds.minBottom, this.bounds.maxBottom)

        if (this.lockAspectRatio && this.resizingOnY) {
          right = this.right - (this.bottom - bottom) * aspectFactor
        }
      } else if (this.handle.includes('t')) {
        top = this.restrictToBounds(mouseClickPosition.top - deltaY, this.bounds.minTop, this.bounds.maxTop)

        if (this.lockAspectRatio && this.resizingOnY) {
          left = this.left - (this.top - top) * aspectFactor
        }
      }

      if (this.handle.includes('r')) {
        right = this.restrictToBounds(mouseClickPosition.right + deltaX, this.bounds.minRight, this.bounds.maxRight)

        if (this.lockAspectRatio && this.resizingOnX) {
          bottom = this.bottom - (this.right - right) / aspectFactor
        }
      } else if (this.handle.includes('l')) {
        left = this.restrictToBounds(mouseClickPosition.left - deltaX, this.bounds.minLeft, this.bounds.maxLeft)

        if (this.lockAspectRatio && this.resizingOnX) {
          top = this.top - (this.left - left) / aspectFactor
        }
      }

      const width = this.computeWidth(this.parentWidth, left, right)
      const height = this.computeHeight(this.parentHeight, top, bottom)

      var onResizeRet = this.onResize(this.handle, left, top, width, height, mouseClickPosition.mouseX - tmpDeltaX)

      if (onResizeRet) {
        var { l, t, r, b, h } = onResizeRet

        this.left = l
        this.top = t
        this.right = r
        this.bottom = b
        this.height = h
      }
    },
    handleDrag(e) {
      const axis = this.axis
      const grid = this.grid
      const bounds = this.bounds
      const mouseClickPosition = this.mouseClickPosition

      const tmpDeltaX = axis && axis !== 'y' ? mouseClickPosition.mouseX - (e.touches ? e.touches[0].pageX : e.pageX) : 0
      const tmpDeltaY = axis && axis !== 'x' ? mouseClickPosition.mouseY - (e.touches ? e.touches[0].pageY : e.pageY) : 0

      const [deltaX, deltaY] = this.snapToGrid(grid, tmpDeltaX, tmpDeltaY, this.scale)

      var leftD = this.restrictToBounds(mouseClickPosition.left - deltaX, bounds.minLeft, bounds.maxLeft)
      var topD = this.restrictToBounds(mouseClickPosition.top - deltaY, bounds.minTop, bounds.maxTop)
      const rightD = this.restrictToBounds(mouseClickPosition.right + deltaX, bounds.minRight, bounds.maxRight)
      const bottomD = this.restrictToBounds(mouseClickPosition.bottom + deltaY, bounds.minBottom, bounds.maxBottom)

      const xMouseDiff = mouseClickPosition.mouseX - mouseClickPosition.left
      //// console.log('bottom',bottomD,'left',leftD,"mouseClickPosition.left", mouseClickPosition)
      //  // console.log("BOUNDS", bounds)
      var onDragRet = this.onDrag(leftD, topD, rightD, bottomD, mouseClickPosition.mouseX - tmpDeltaX)

      if (onDragRet) {
        var { l, t, r, b, h } = onDragRet
        this.left = Math.round(l)
        this.top = t
        this.right = r
        this.bottom = b
        this.height = h
        this.dragging = true
      }
    },
  },
}
</script>
